import { InputColumnTableProps, InputTable, Modal, TextArea } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { MultiSelect } from 'primereact/multiselect'
import { useEffect, useMemo, useState } from 'react'
import { ComboDataDTO } from '../../../dto/ComboDataDTO'
import { HealthEntityNoteDTO } from '../../../dto/HealthEntityNoteDTO'
import { useHealthEntityService } from '../../../servicesHooks/HealthEntity/useHealthEntityService'
import LocationInlineEditActions from '../../Location/components/LocationInlineEditActions'
import HealthEntityActionMenu, { HealthEntityActionMenuItem } from '../components/HealthEntityActionMenu'

interface HealthEntityNotesTabProps {
  notes: HealthEntityNoteDTO[]
  setNotes: React.Dispatch<React.SetStateAction<HealthEntityNoteDTO[]>>
  isMobile?: boolean
  readOnly?: boolean
}

export default function HealthEntityNotesTab({
  notes,
  setNotes,
  isMobile = false,
  readOnly = false,
}: Readonly<HealthEntityNotesTabProps>) {
  const { getContractModalities } = useHealthEntityService()
  const [modalities, setModalities] = useState<ComboDataDTO[]>([])
  const [noteItem, setNoteItem] = useState<HealthEntityNoteDTO>({
    lognote: null,
    note: '',
    modalityIds: [],
    modalityLabel: '',
  })
  const [noteErrors, setNoteErrors] = useState<{ note?: string; modalityIds?: string }>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [mobileModalVisible, setMobileModalVisible] = useState(false)

  useEffect(() => {
    getContractModalities({ filterValue: '', offSet: 0, pageSize: 100 }).then((resp) => {
      const normalized = (resp ?? [])
        .map((item) => {
          const itemId = Number(item.id ?? -1)
          const itemName = (item.name ?? '').trim()
          return {
            id: itemId,
            name: itemName,
          }
        })
        .filter((item) => {
          const normalizedName = (item.name ?? '').trim().toLowerCase()
          return item.id > 0 && normalizedName !== '' && normalizedName !== 'todas' && item.id !== 99
        })
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      setModalities(normalized)
    }).catch(() => {
      setModalities([])
    })
  }, [])

  const normalizedNotes = useMemo(() => notes ?? [], [notes])

  function modalityLabel(values: number[] | undefined) {
    const normalized = values ?? []
    if (normalized.length === 0) {
      return ''
    }
    return modalities
      .filter(item => normalized.includes(item.id ?? -1))
      .map(item => item.name ?? '')
      .join(', ')
  }

  function resolveDisplayedModalityLabel(item: HealthEntityNoteDTO) {
    const selectedIds = item.modalityIds ?? []
    if (selectedIds.length > 0) return modalityLabel(selectedIds)

    const legacyLabel = (item.modalityLabel ?? '').trim().toLowerCase()
    if (legacyLabel === 'todas') {
      const allModalityIds = modalities
        .map(modality => Number(modality.id ?? -1))
        .filter(id => id > 0)
      return modalityLabel(allModalityIds)
    }

    return item.modalityLabel ?? ''
  }

  function clearEditor() {
    setNoteItem({
      lognote: null,
      note: '',
      modalityIds: [],
      modalityLabel: '',
    })
    setEditIndex(null)
    setNoteErrors({})
  }

  function closeMobileModal() {
    setMobileModalVisible(false)
    clearEditor()
  }

  async function addOrUpdateNote() {
    const errors: { note?: string; modalityIds?: string } = {}
    if ((noteItem.note ?? '').trim() === '') {
      errors.note = 'La nota es obligatoria'
    }
    if ((noteItem.modalityIds ?? []).length === 0) {
      errors.modalityIds = 'Debe seleccionar al menos una modalidad'
    }

    if (Object.keys(errors).length > 0) {
      setNoteErrors(errors)
      return false
    }

    const normalizedModalityIds = noteItem.modalityIds ?? []
    const normalizedItem: HealthEntityNoteDTO = {
      ...noteItem,
      note: noteItem.note.trim(),
      modalityIds: normalizedModalityIds,
      modalityLabel: modalityLabel(normalizedModalityIds),
    }

    if (editIndex === null) {
      setNotes(prev => [...prev, normalizedItem])
    } else {
      setNotes(prev => prev.map((item, index) => (index === editIndex ? normalizedItem : item)))
    }
    clearEditor()
    if (isMobile) {
      setMobileModalVisible(false)
    }
    return true
  }

  function handleEdit(item: HealthEntityNoteDTO, index: number) {
    const isAllModalities = (item.modalityLabel ?? '').trim().toLowerCase() === 'todas'
    const allModalityIds = modalities
      .map(modality => Number(modality.id ?? -1))
      .filter(id => id > 0)
    const normalizedModalityIds = isAllModalities && (item.modalityIds ?? []).length === 0
      ? allModalityIds
      : (item.modalityIds ?? [])

    setNoteItem({
      ...item,
      lognote: item.lognote ?? null,
      note: item.note ?? '',
      modalityIds: Array.from(new Set(normalizedModalityIds)),
      modalityLabel: resolveDisplayedModalityLabel(item),
    })
    setNoteErrors({})
    setEditIndex(index)
  }

  function handleDelete(index: number) {
    setNotes(prev => prev.filter((_note, i) => i !== index))
    if (editIndex === null) return
    if (editIndex === index) {
      clearEditor()
      return
    }
    if (index < editIndex) {
      setEditIndex(editIndex - 1)
    }
  }

  function openAddModal() {
    clearEditor()
    setMobileModalVisible(true)
  }

  function openEditModal(item: HealthEntityNoteDTO, index: number) {
    handleEdit(item, index)
    setMobileModalVisible(true)
  }

  function formatCardValue(value: string | null | undefined, emptyText = 'Sin datos') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  const columns: InputColumnTableProps<HealthEntityNoteDTO>[] = [
    {
      header: 'Modalidad',
      className: 'w-4',
      body: (item) => resolveDisplayedModalityLabel(item),
      input: (
        <div className='w-full pt-1'>
          <MultiSelect
            className={`w-full ${noteErrors.modalityIds ? 'p-invalid' : ''}`}
            options={modalities}
            optionLabel='name'
            optionValue='id'
            value={noteItem.modalityIds ?? []}
            onChange={(e) => {
              const selectedValues = (e.value ?? []) as number[]
              const normalizedValues = Array.from(new Set(selectedValues.filter((id) => Number(id) > 0)))
              setNoteItem(prev => ({ ...prev, modalityIds: normalizedValues }))
              setNoteErrors(prev => ({ ...prev, modalityIds: undefined }))
            }}
            placeholder='Seleccionar'
            showClear
            filter
            maxSelectedLabels={999}
            display='chip'
          />
          {noteErrors.modalityIds && <small className='p-error'>{noteErrors.modalityIds}</small>}
        </div>
      ),
    },
    {
      header: 'Nota',
      className: 'w-8',
      body: (item) => item.note ?? '',
      input: (
        <div className='w-full pt-1'>
          <TextArea
            className={`${noteErrors.note ? '' : 'border-grey'} w-full`}
            value={noteItem.note ?? ''}
            onChange={(e) => {
              setNoteItem(prev => ({ ...prev, note: e.target.value }))
              setNoteErrors(prev => ({ ...prev, note: undefined }))
            }}
            error={!!noteErrors.note}
            rows={4}
            autoResize
          />
          {noteErrors.note && <small className='p-error'>{noteErrors.note}</small>}
        </div>
      ),
    },
    {
      header: '',
      className: 'health-entity-inline-action-column',
      input: (
        <LocationInlineEditActions
          visible={!readOnly && editIndex !== null}
          onConfirm={() => { void addOrUpdateNote() }}
          onCancel={clearEditor}
        />
      ),
      body: (item, index) => {
        if (readOnly) return null
        const menuItems: HealthEntityActionMenuItem[] = [
          {
            label: 'Editar',
            icon: 'pi pi-file-edit',
            action: () => handleEdit(item, index),
          },
          {
            label: 'Eliminar',
            icon: 'pi pi-trash',
            action: () => handleDelete(index),
          },
        ]

        return (
          <div className='flex justify-content-end'>
            <HealthEntityActionMenu
              menuItems={menuItems}
              buttonClassName='p-button-text'
              buttonAriaLabel='Opciones de la nota'
              contentClassName='w-64'
            />
          </div>
        )
      },
    },
  ]

  if (!isMobile) {
    return (
      <div className='health-entity-table-tab'>
        <InputTable
          columnGap={4}
          iconAddBtn='pi pi-plus-circle text-3xl'
          headerDivClassName='font-semibold bg-bluegray-50'
          dividerClassName='my-1 border-divider'
          itemsClassName='health-entity-inputtable-item-card'
          buttonColumnWidth={43}
          items={normalizedNotes}
          columns={columns}
          onAdd={readOnly ? undefined : addOrUpdateNote}
          divBtnClassName={!readOnly && editIndex !== null ? 'health-entity-inline-editing-hide-add' : ''}
          disabled={readOnly}
          divider
          dividerInFirstItem
        />
      </div>
    )
  }

  return (
    <div className='health-entity-table-tab'>
      <div className='health-entity-mobile-section-header'>
        <h4 className='health-entity-section-title'>Notas</h4>
        {!readOnly && (
          <Button
            className='health-entity-mobile-add-btn'
            icon='pi pi-plus-circle'
            label='Agregar'
            onClick={openAddModal}
            aria-label='Agregar nota'
          />
        )}
      </div>

      <div className='health-entity-mobile-section-list'>
        {normalizedNotes.map((item, index) => {
          const menuItems: HealthEntityActionMenuItem[] = readOnly
            ? []
            : [
              {
                label: 'Editar',
                icon: 'pi pi-file-edit',
                action: () => openEditModal(item, index),
              },
              {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                action: () => handleDelete(index),
              },
            ]

          return (
            <article
              key={`health-entity-note-${item.lognote ?? index}-${index}`}
              className='health-entity-mobile-record-card health-entity-mobile-note-plan-card'
            >
              <div className='health-entity-mobile-record-header'>
                <h4 className='health-entity-mobile-record-title'>{formatCardValue(resolveDisplayedModalityLabel(item))}</h4>
                {!readOnly && (
                  <HealthEntityActionMenu
                    menuItems={menuItems}
                    buttonClassName='p-button-text health-entity-mobile-record-menu-btn'
                    buttonAriaLabel='Opciones de la nota'
                  />
                )}
              </div>

              <div className='health-entity-mobile-record-body'>
                <p className='health-entity-mobile-record-note'>{formatCardValue(item.note)}</p>
              </div>
            </article>
          )
        })}

        {normalizedNotes.length === 0 && (
          <p className='health-entity-mobile-empty'>No hay notas cargadas.</p>
        )}
      </div>

      <Modal
        className='health-entity-mobile-editor-modal'
        visible={mobileModalVisible}
        setVisible={setMobileModalVisible}
        width='w-medium'
        onHideCustom={closeMobileModal}
        header={<h1 className='info-dark text-2xl'>{editIndex === null ? 'Agregar nota' : 'Editar nota'}</h1>}
        footerButtonLeftText='Cancelar'
        onClickLeftBtn={closeMobileModal}
        footerButtonRightText={editIndex === null ? 'Agregar' : 'Guardar'}
        onClickRightBtn={() => { void addOrUpdateNote() }}
      >
        <div className='health-entity-mobile-modal-content'>
          <div className='health-entity-general-field'>
            <label className='block mb-2'>Modalidad <span className='text-red-500'>*</span></label>
            <MultiSelect
              className={`w-full ${noteErrors.modalityIds ? 'p-invalid' : ''}`}
              options={modalities}
              optionLabel='name'
              optionValue='id'
              value={noteItem.modalityIds ?? []}
              onChange={(e) => {
                const selectedValues = (e.value ?? []) as number[]
                const normalizedValues = Array.from(new Set(selectedValues.filter((id) => Number(id) > 0)))
                setNoteItem(prev => ({ ...prev, modalityIds: normalizedValues }))
                setNoteErrors(prev => ({ ...prev, modalityIds: undefined }))
              }}
              placeholder='Seleccionar'
              showClear
              filter
              maxSelectedLabels={999}
              display='chip'
            />
            {noteErrors.modalityIds && <small className='p-error'>{noteErrors.modalityIds}</small>}
          </div>

          <div className='health-entity-general-field'>
            <label className='block mb-2'>Nota <span className='text-red-500'>*</span></label>
            <TextArea
              className={`${noteErrors.note ? '' : 'border-grey'} w-full`}
              value={noteItem.note ?? ''}
              onChange={(e) => {
                setNoteItem(prev => ({ ...prev, note: e.target.value }))
                setNoteErrors(prev => ({ ...prev, note: undefined }))
              }}
              error={!!noteErrors.note}
              rows={5}
              autoResize
            />
            {noteErrors.note && <small className='p-error'>{noteErrors.note}</small>}
          </div>

        </div>
      </Modal>
    </div>
  )
}
